import React from 'react';
import { Star, Image as ImageIcon, Video } from 'lucide-react';
import { Review } from '../../types';

interface ReviewStatsProps {
  reviews: Review[];
  averageRating: number;
  totalMedia: number;
  onShowMediaGallery: () => void;
}

const ReviewStats: React.FC<ReviewStatsProps> = ({
  reviews,
  averageRating,
  totalMedia,
  onShowMediaGallery
}) => {
  // Подсчет отзывов по рейтингам
  const ratingCounts = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: reviews.filter(review => review.rating === rating).length,
    percentage: reviews.length > 0 ? (reviews.filter(review => review.rating === rating).length / reviews.length) * 100 : 0
  }));

  const formatRating = (rating: number) => {
    return rating.toFixed(1);
  };

  const renderStars = (rating: number, size: 'sm' | 'lg' = 'sm') => {
    const starSize = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Общий рейтинг */}
        <div className="text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start space-x-4 mb-2">
            <span className="text-4xl font-bold text-gray-900 dark:text-white">
              {formatRating(averageRating)}
            </span>
            <div>
              {renderStars(averageRating, 'lg')}
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Основано на {reviews.length} отзыв{reviews.length === 1 ? 'е' : reviews.length < 5 ? 'ах' : 'ах'}
              </p>
            </div>
          </div>
        </div>

        {/* Распределение рейтингов */}
        <div className="space-y-2">
          {ratingCounts.map(({ rating, count, percentage }) => (
            <div key={rating} className="flex items-center space-x-3">
              <div className="flex items-center space-x-1 w-12">
                <span className="text-sm text-gray-600 dark:text-gray-400">{rating}</span>
                <Star className="w-3 h-3 text-yellow-400 fill-current" />
              </div>
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400 w-8 text-right">
                {count}
              </span>
            </div>
          ))}
        </div>

        {/* Медиа галерея */}
        {totalMedia > 0 && (
          <div className="text-center lg:text-right">
            <button
              onClick={onShowMediaGallery}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 
                       text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30
                       transition-colors duration-200"
            >
              <div className="flex items-center space-x-1">
                <ImageIcon className="w-4 h-4" />
                <Video className="w-4 h-4" />
              </div>
              <span className="font-medium">
                {totalMedia} фото и видео
              </span>
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              от покупателей
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewStats;
